import { create } from "zustand";

/**
 * Player settings: sound, motion, and how hard to push the GPU.
 *
 * These are preferences, not game state, so they live apart from the run and
 * outlive it — turning the klaxons off should not be something you have to do
 * again after a bad shift. Same localStorage discipline as runStore: every
 * access is wrapped, because a game that will not start because it could not
 * read a preference is worse than one that forgets your preference.
 */

const SETTINGS_KEY = "reagent.settings.v1";

/**
 * Three tiers rather than a checkbox per effect.
 *
 *  - high:   everything — full post stack, retina, dust motes.
 *  - medium: no grain or vignette, capped device pixel ratio. The bands and
 *            outlines that make the game look like itself all survive.
 *  - low:    no post-processing at all. This is the tier that has to work on
 *            an integrated GPU, so it gives up bloom — which costs the glass
 *            tubes their glow, and is the reason it is not the default.
 */
export type Quality = "high" | "medium" | "low";

export interface Settings {
  readonly quality: Quality;
  readonly muted: boolean;
  /** Master volume, 0..1. */
  readonly volume: number;
  /**
   * Damps camera parallax, dust drift and effect travel. Defaults to the OS
   * preference — someone who has asked their system for less motion has
   * already answered this question once.
   */
  readonly reduceMotion: boolean;
  /** Frame-time readout in the tuning console. */
  readonly showPerf: boolean;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function defaults(): Settings {
  return {
    quality: "high",
    muted: false,
    volume: 0.7,
    reduceMotion: prefersReducedMotion(),
    showPerf: false,
  };
}

const QUALITIES: readonly Quality[] = ["high", "medium", "low"];

/**
 * Reads a saved settings blob field by field. An unrecognised or corrupt save
 * falls back per-field rather than wholesale, so a settings file written by a
 * later version still contributes what this version understands.
 */
function readSettings(): Settings {
  const base = defaults();
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return base;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return base;
    const s = parsed as Partial<Record<keyof Settings, unknown>>;
    return {
      quality: QUALITIES.includes(s.quality as Quality) ? (s.quality as Quality) : base.quality,
      muted: typeof s.muted === "boolean" ? s.muted : base.muted,
      volume:
        typeof s.volume === "number" && s.volume >= 0 && s.volume <= 1 ? s.volume : base.volume,
      reduceMotion: typeof s.reduceMotion === "boolean" ? s.reduceMotion : base.reduceMotion,
      showPerf: typeof s.showPerf === "boolean" ? s.showPerf : base.showPerf,
    };
  } catch {
    return base;
  }
}

function writeSettings(settings: Settings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Nothing to do and nothing worth interrupting the player for.
  }
}

interface SettingsStore extends Settings {
  set: (patch: Partial<Settings>) => void;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  ...readSettings(),
  set: (patch) => {
    const next = { ...current(get()), ...patch };
    writeSettings(next);
    set(patch);
  },
}));

function current(store: SettingsStore): Settings {
  const { quality, muted, volume, reduceMotion, showPerf } = store;
  return { quality, muted, volume, reduceMotion, showPerf };
}

/** Non-reactive read, for the audio engine and other non-React callers. */
export function settings(): Settings {
  return current(useSettingsStore.getState());
}
